import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import useApi from 'helpers/api';
import { processHtmlForEmail } from 'helpers/utils';
import useNotification from 'hooks/useNotification';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Button, Card, Dropdown, Form } from 'react-bootstrap';

const AddNoteComponent = ({
  show,
  handleClose,
  data = {},
  refreshData,
  refreshNotes,
  refreshLogs,
  ticket
}) => {
  if (!show) return null; // Não renderiza se show=false

  const api = useApi();
  const form = useRef(null);
  const editorRef = useRef(null);
  const [validated, setValidated] = useState(false);
  const [body, setBody] = useState({
    note: '',
    typeView: 'PRIVATE'
  });
  const [isEdit, setIsEdit] = useState(false);
  const [title, setTitle] = useState('');
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [loading, setLoading] = useState(false);

  const ticketId = ticket.id;

  useEffect(() => {
    if (data === undefined) data = {};

    if (Object.keys(data).length !== 0) {
      setTitle('Editar nota');
      setIsEdit(true);
      handleSetBody('typeView', data.typeView);
      handleSetBody('note', data.note);
    } else {
      setTitle('Adicionar nota');
      setIsEdit(false);
      clearFields();
    }

    setValidated(false);
  }, [data]);

  const onSubmit = async e => {
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.note.trim() === '' ||
      body.note === '<p><br></p>'
    ) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
      setLoading(false);
      return;
    }

    const processedNote = processHtmlForEmail(body.note);
    const processedBody = { ...body, note: processedNote };

    let response;
    if (isEdit) {
      response = await api.ticketNotes.edit(ticketId, data.id, processedBody);
      showSuccessNotification('Nota editada com sucesso');
    } else {
      response = await api.ticketNotes.add(ticketId, processedBody);
      showSuccessNotification('Nota adicionada com sucesso');
    }

    handleResponse(response);
    clearFields();
    handleClose();
    setLoading(false);
  };

  const addAndSetAsPending = async () => {
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.note.trim() === '' ||
      body.note === '<p><br></p>'
    ) {
      setValidated(true);
      setLoading(false);
      return;
    }

    let response = await api.ticketNotes.addAndSetAsPending(ticketId, body);
    showSuccessNotification('Nota adicionada com sucesso');
    handleResponse(response);
    if (refreshData) refreshData();
    clearFields();
    handleClose();
    setLoading(false);
  };

  const addAndSetAsCompleted = async () => {
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.note.trim() === '' ||
      body.note === '<p><br></p>'
    ) {
      setValidated(true);
      setLoading(false);
      return;
    }

    let response = await api.ticketNotes.addAndSetAsCompleted(ticketId, body);
    showSuccessNotification('Nota adicionada com sucesso');
    handleResponse(response);
    if (refreshData) refreshData();
    clearFields();
    handleClose();
    setLoading(false);
  };

  const handleSetBody = (fieldName, fieldValue) => {
    setBody(prevBody => ({ ...prevBody, [fieldName]: fieldValue }));
  };

  function handleResponse(response) {
    if (response.id) {
      showSuccessNotification('Nota adicionada com sucesso');
      refreshLogs();
      refreshNotes();
      if (refreshData) refreshData();
    } else {
      showErrorNotification(response.userMessage);
    }
  }

  const clearFields = () => {
    setBody({
      note: '',
      typeView: 'PRIVATE'
    });
  };

  return (
    <>
      {loading && <Loading />}

      <Card>
        <Card.Body className="pb-0">
          <Form ref={form} validated={validated} noValidate>
            <div className="mb-2">
              <Form.Label className="mb-1 fs-10">
                Visualização <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                required
                size="sm"
                value={body.typeView}
                onChange={e => handleSetBody('typeView', e.target.value)}
              >
                <option value="PRIVATE">Privado</option>
                <option value="PUBLIC">Público</option>
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Selecione um tipo de visualização.
              </Form.Control.Feedback>
            </div>
            <div className="mb-2">
              <Form.Label className="mb-1 fs-10 d-flex align-items-center justify-content-between">
                <span>
                  Descrição <span className="text-danger">*</span>
                </span>
                <MessageTemplateSelector
                  type="NOTE"
                  ticketId={ticketId}
                  editorRef={editorRef}
                  onTemplateApplied={content => {
                    handleSetBody('note', content);
                  }}
                />
              </Form.Label>
              <Editor
                value={value => handleSetBody('note', value)}
                height="300px"
                initialValue={body.note}
                required
                data={data.note}
                ref={editorRef}
              />
              <MessageTemplateAutocomplete
                editorRef={editorRef}
                type="NOTE"
                ticketId={ticketId}
                onTemplateApplied={content => {
                  handleSetBody('note', content);
                }}
              />
              {validated &&
                (body.note.trim() === '' || body.note === '<p><br></p>') && (
                  <div className="text-danger fs-10 mt-1">
                    Campo obrigatório.
                  </div>
                )}
            </div>
          </Form>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-end">
          <Button
            variant="falcon-default"
            type="button"
            className="me-2"
            onClick={handleClose}
          >
            Fechar
          </Button>
          {!isEdit ? (
            <>
              <Button
                variant="primary"
                type="button"
                onClick={onSubmit}
                style={{ borderRadius: '20px 0px 0px 20px' }}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>

              <Dropdown>
                <Dropdown.Toggle
                  as={CustomToggle}
                  style={{ borderRadius: '0px 20px 20px 0px', height: '100%' }}
                  disabled={loading}
                ></Dropdown.Toggle>

                <Dropdown.Menu className="flex-wrap" style={{ width: '150px' }}>
                  <Dropdown.Item
                    onClick={addAndSetAsPending}
                    disabled={ticket.status && ticket.status.status !== 'TO_DO'}
                  >
                    {loading ? 'Salvando...' : 'Salvar e Pausar'}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={addAndSetAsCompleted}
                    disabled={
                      ticket.status && ticket.status.status === 'COMPLETED'
                    }
                  >
                    {loading ? 'Salvando...' : 'Salvar e Concluir'}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          ) : (
            <Button
              variant="primary"
              type="button"
              onClick={onSubmit}
              disabled={loading}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          )}
        </Card.Footer>
      </Card>
    </>
  );
};

const CustomToggle = forwardRef(({ onClick, disabled }, ref) => (
  <Button
    style={{ borderRadius: '0 20px 20px 0', height: '100%' }}
    ref={ref}
    disabled={disabled}
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faChevronDown} />
  </Button>
));

export default AddNoteComponent;
